import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  Appointment,
  CalMessage,
  CalendarSummary,
  Folder,
  Invite,
  InviteComponent,
  Mountpoint,
} from '@zimbra-webif/sdk';
import { useClient } from './ClientContext';

/** A calendar (own appointment folder or shared appointment mountpoint). */
export interface CalendarFolder {
  id: string;
  name: string;
  /** true if this is a shared mountpoint (link) from another account */
  shared: boolean;
  /** owner email, present on shared calendars */
  owner?: string;
  /** Zimbra explicit rgb (#rrggbb), if the server set one */
  rgb?: string;
  /** Zimbra numeric color index (0-9) the server assigned */
  color?: number;
  /**
   * For SHARED mountpoints: the owner account zid. Appointment Search hits from a
   * shared calendar carry `l` = "<zid>:<rid>" rather than the local mountpoint id.
   */
  zid?: string;
  /** For SHARED mountpoints: the remote folder id in the owner's mailbox. */
  rid?: string;
}

/** Collect every appointment-view calendar — own folders AND shared mountpoints (link[]). */
function collectCalendars(root: Folder | undefined): CalendarFolder[] {
  const out: CalendarFolder[] = [];
  const walk = (folders: Folder[] | undefined, links: Mountpoint[] | undefined) => {
    for (const f of folders ?? []) {
      if (f.view === 'appointment') {
        out.push({
          id: f.id,
          name: f.name ?? 'Calendar',
          shared: false,
          rgb: typeof f.rgb === 'string' ? f.rgb : undefined,
          color: typeof f.color === 'number' ? f.color : undefined,
        });
      }
      walk(f.folder, f.link);
    }
    for (const l of links ?? []) {
      if (l.view === 'appointment') {
        out.push({
          id: l.id,
          name: l.name ?? 'Calendar',
          shared: true,
          owner: typeof l.owner === 'string' ? l.owner : undefined,
          rgb: typeof l.rgb === 'string' ? l.rgb : undefined,
          color: typeof l.color === 'number' ? l.color : undefined,
          // zid is a string (uuid); rid comes back as a NUMBER — coerce both so
          // the `<zid>:<rid>` event-color mapping matches the appt's `l` value.
          zid: l.zid != null ? String(l.zid) : undefined,
          rid: l.rid != null ? String(l.rid) : undefined,
        });
      }
      walk(l.folder, l.link);
    }
  };
  walk(root?.folder, root?.link);
  return out;
}

/**
 * The list of calendars (own appointment folders + shared mountpoints) from
 * `getFolderTree`. Drives the Calendars side pane and the search query.
 */
export function useCalendarFolders() {
  const { client } = useClient();
  return useQuery({
    queryKey: ['calendar-folders'],
    queryFn: async (): Promise<CalendarFolder[]> => {
      const tree = await client.mail.getFolderTree();
      return collectCalendars(tree.folder?.[0]);
    },
  });
}

/**
 * A calendar Search hit (`<appt>`). The mail `Search` command returns these when
 * called with `types: 'appointment'` + `calExpandInstStart/End`; the wire shape
 * is the `CalendarSummary` (LegacyCalendaringData-style) entity: name/loc/dur/
 * allDay/`l` (folder id) plus expanded `inst[]` (each `s` = instance start, ms epoch).
 */
export type CalendarApptHit = CalendarSummary;

/**
 * Appointments whose instances fall in [startMs, endMs), restricted to the given
 * enabled calendar folder ids. Backed by the mail `Search` command (the
 * documented calendar LIST path — GetApptSummaries is deprecated server-side).
 * The query is the OR of the enabled calendars (`inid:10 OR inid:1308 …`), with a
 * fallback to the default `Calendar` when none are passed. The raw response is a
 * SOAP JsonObject; we cast its `appt` array exactly like Mailbox casts `m`.
 */
export function useCalendarAppts(startMs: number, endMs: number, folderIds: string[]) {
  const { client } = useClient();
  return useQuery({
    queryKey: ['calendar-appts', startMs, endMs, folderIds.join(',')],
    queryFn: async (): Promise<CalendarApptHit[]> => {
      const query = folderIds.length
        ? folderIds.map((id) => `inid:${id}`).join(' OR ')
        : 'in:"Calendar"';
      const res = await client.mail.search({
        query,
        types: 'appointment',
        calExpandInstStart: startMs,
        calExpandInstEnd: endMs,
        limit: 1000,
      });
      return ((res as { appt?: CalendarApptHit[] }).appt ?? []) as CalendarApptHit[];
    },
  });
}

/**
 * Full detail for one appointment (all invites + attendee replies), via
 * `calendar.getAppointment`. The response carries `appt` as either a single
 * object or an array; we normalize to the first appointment.
 */
export function useAppointment(id: string | null) {
  const { client } = useClient();
  return useQuery({
    queryKey: ['appointment', id],
    enabled: id !== null,
    queryFn: async (): Promise<Appointment | undefined> => {
      const res = await client.calendar.getAppointment({ id: id as string });
      const appt = res.appt;
      if (Array.isArray(appt)) return appt[0];
      return appt;
    },
  });
}

const pad2 = (n: number) => String(n).padStart(2, '0');
/** `YYYYMMDDThhmmssZ` (UTC) — the DATETIME form Zimbra accepts for timed events. */
function toUtcStamp(d: Date): string {
  return (
    `${d.getUTCFullYear()}${pad2(d.getUTCMonth() + 1)}${pad2(d.getUTCDate())}` +
    `T${pad2(d.getUTCHours())}${pad2(d.getUTCMinutes())}${pad2(d.getUTCSeconds())}Z`
  );
}
/** `YYYYMMDD` (local) — the date-only form marking an all-day boundary. */
function toDateOnly(d: Date): string {
  return `${d.getFullYear()}${pad2(d.getMonth() + 1)}${pad2(d.getDate())}`;
}

export interface EditAppointmentInput {
  id: string;
  name: string;
  loc: string;
  /** start/end as ms epoch */
  startMs: number;
  endMs: number;
  notes: string;
}

/**
 * Edit an appointment via ModifyAppointment. Mirrors the ModifyTask path: refetch
 * with `sync` to get the item's `ms`/`rev` for conflict detection, then resend the
 * fetched component with the edited fields (spreading `comp` preserves organizer,
 * attendees, recurrence, alarms, …). Editing a series modifies the default invite.
 */
export function useModifyAppointment() {
  const { client } = useClient();
  const qc = useQueryClient();
  // One fetch+modify pass. `id` is the COMPOSITE invite id `<calItemId>-<inviteId>`
  // and ms/rev are the freshly-fetched modseq — both verified against the classic
  // client's ZmCalItem (request.id = invId; request.ms/rev = message modseq).
  const doModify = async (input: EditAppointmentInput) => {
    const res = await client.calendar.getAppointment({ id: input.id, sync: true });
    const appt = Array.isArray(res.appt) ? res.appt[0] : res.appt;
    const inv: Invite | undefined = appt?.inv?.[0];
    const comp: InviteComponent | undefined = inv?.comp?.[0];
    if (!appt || !inv || !comp || inv.id === undefined) {
      throw new Error('Appointment has no editable invite');
    }
    const ms = typeof appt.ms === 'number' ? appt.ms : 0;
    const rev = typeof appt.rev === 'number' ? appt.rev : 0;
    const allDay = comp.allDay === '1';

    const nextComp: InviteComponent = {
      ...comp,
      name: input.name,
      loc: input.loc,
      desc: input.notes,
    };
    delete nextComp.descHtml; // we send plaintext desc; drop stale HTML copy
    delete nextComp.dur; // we set an explicit end below (dur + e conflict)
    if (allDay) {
      nextComp.s = { d: toDateOnly(new Date(input.startMs)) };
      nextComp.e = { d: toDateOnly(new Date(input.endMs)) };
    } else {
      nextComp.s = { d: toUtcStamp(new Date(input.startMs)) };
      nextComp.e = { d: toUtcStamp(new Date(input.endMs)) };
    }

    return client.calendar.modifyAppointment({
      id: `${appt.id ?? input.id}-${inv.id}`,
      comp: typeof comp.compNum === 'number' ? comp.compNum : 0,
      ms,
      rev,
      message: {
        su: input.name,
        inv: { tz: inv.tz, comp: [nextComp] },
      },
    });
  };
  return useMutation({
    mutationFn: async (input: EditAppointmentInput) => {
      try {
        return await doModify(input);
      } catch (e) {
        // Stale modseq → re-fetch & retry once (mirrors ZmCalItem conflict handling).
        if (/out of date|INVITE_OUT_OF_DATE/i.test((e as Error)?.message ?? '')) {
          return await doModify(input);
        }
        throw e;
      }
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['calendar-appts'] });
      void qc.invalidateQueries({ queryKey: ['appointment', vars.id] });
    },
  });
}

export interface CreateAppointmentInput {
  /** local calendar folder id — own folder or a shared mountpoint (Zimbra proxies to the owner) */
  folderId: string;
  name: string;
  loc: string;
  /** start/end as ms epoch; for all-day events `endMs` is the start of the day AFTER the last day */
  startMs: number;
  endMs: number;
  allDay: boolean;
  notes: string;
  /** attendee addresses — each gets an invitation mail */
  attendees: string[];
  /** the signed-in user's address (organizer, or sentBy on a shared calendar) */
  me: string;
  /** owner of a shared calendar: becomes the organizer, with `me` as sentBy (mirrors ZmCalItem) */
  owner?: string;
}

/**
 * Create an appointment via CreateAppointment. Field shape follows the classic
 * client's ZmCalItem request: organizer = me on own calendars, = the calendar's
 * owner with `sentBy` = me on shared ones; attendees go both in `<comp><at>` and
 * as `<e t="t">` recipients so the server mails the invitation.
 */
export function useCreateAppointment() {
  const { client } = useClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateAppointmentInput) => {
      const comp: InviteComponent = {
        name: input.name,
        loc: input.loc,
        allDay: input.allDay ? '1' : '0',
        status: 'CONF',
        fb: 'B',
        transp: 'O',
        class: 'PUB',
        or: input.owner && input.owner !== input.me
          ? { a: input.owner, sentBy: input.me }
          : { a: input.me },
      };
      if (input.allDay) {
        // Zimbra's all-day `e` is the LAST day (inclusive), not the exclusive end.
        const last = new Date(input.endMs);
        last.setDate(last.getDate() - 1);
        comp.s = { d: toDateOnly(new Date(input.startMs)) };
        comp.e = { d: toDateOnly(last < new Date(input.startMs) ? new Date(input.startMs) : last) };
      } else {
        comp.s = { d: toUtcStamp(new Date(input.startMs)) };
        comp.e = { d: toUtcStamp(new Date(input.endMs)) };
      }
      if (input.notes) comp.desc = { _content: input.notes };
      if (input.attendees.length) {
        comp.at = input.attendees.map((a) => ({ a, role: 'REQ', ptst: 'NE', rsvp: '1' }));
      }
      const message: CalMessage = {
        l: input.folderId,
        su: input.name,
        inv: { comp: [comp] },
      };
      if (input.attendees.length) {
        message.e = input.attendees.map((a) => ({ a, t: 't' }));
        message.mp = [{ ct: 'text/plain', content: { _content: input.notes } }];
      }
      return client.calendar.createAppointment({ message });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['calendar-appts'] });
    },
  });
}
